'use client';
import React, { Suspense, useState } from 'react';

import { defaultDropdownStyles } from '../../components/Dropdown/constants';
import { Dropdown } from '../../components/Dropdown/Dropdown';
import { ChevronDownIcon } from '../../components/Icons/ChevronDownIcon';
import { useUserInfo } from '../../helpers/hooks/useUserInfo';
import { getFullName } from '../../helpers/user/getFullName';
import { GetProfile_user } from '../../interfaces/graphql';

import imagePlaceholder from './assets/imagePlaceholder.svg';
import { AnglerCatchesView } from './components/AnglerCatchesView/AnglerCatchesView';
import { AnglerDetails } from './components/AnglerDetails/AnglerDetails';
import { AnglerPostsView } from './components/AnglerPostsView/AnglerPostsView';
import { AnglerViewOptions, ANGLER_VIEW_OPTIONS } from './constants';
import { AnglerSchema } from './components/AnglerSchema/AnglerSchema';
import { AnglerPostsViewLoader } from './components/AnglerPostsView/AnglerPostsViewLoader';
import { AnglerCatchesViewLoader } from './components/AnglerCatchesView/AnglerCatchesViewLoader';

export interface IAnglerProps {
  angler: GetProfile_user;
  blogSlug: string | null;
}

export const Angler = ({ angler, blogSlug }: IAnglerProps) => {
  const { user } = useUserInfo();
  const [sortBy, setSortBy] = useState<AnglerViewOptions['value']>('catches');
  const {
    nickname,
    avatar,
    coverImage,
    countryCode,
    biography,
    _id: anglerId,
    isPremium,
    catches,
  } = angler;

  const checkedAvatarImage = avatar?.url ?? imagePlaceholder.src;
  const fullName = getFullName(angler);

  const usersOwnPage = (user && user._id === angler._id) ?? false;
  const currentUserNickname = user ? user.nickname : null;
  const totalAnglerCatches = catches.totalCount;

  return (
    <div className="mx-auto block max-w-(--breakpoint-lg) md:mx-auto md:mt-12 md:flex md:justify-center md:gap-8">
      <AnglerSchema fullName={fullName} nickname={nickname} avatarUrl={checkedAvatarImage} />
      <AnglerDetails
        coverImage={coverImage}
        avatar={checkedAvatarImage}
        nickname={nickname}
        fullName={fullName}
        countryCode={countryCode}
        biography={biography}
        anglerId={anglerId}
        currentUserNickname={currentUserNickname}
        isPremium={isPremium ?? false}
        blogSlug={blogSlug}
        totalAnglerCatches={totalAnglerCatches}
      />
      <div className="mx-4 sm:mx-auto sm:w-1/2 md:mx-0">
        <Dropdown
          customLabelComponent={<></>}
          defaultOption={ANGLER_VIEW_OPTIONS.find(({ value }) => value === sortBy)!}
          options={ANGLER_VIEW_OPTIONS}
          onChange={({ value }) => {
            setSortBy(value);
          }}
          dropdownIcon={<ChevronDownIcon />}
          dropdownStyles={{
            ...defaultDropdownStyles,
            previewOptionClasses: 'sm:mr-3 body-text-md font-heading! flex md:sticky md:top-0',
            openedListboxClasses:
              'left-3 block absolute bg-sea-foam z-mid shadow-md mt-0 py-2 rounded-lg outline-hidden focus:ring-1 focus:border-tuna-shade',
          }}
        />
        {sortBy === 'catches' && (
          <Suspense fallback={<AnglerCatchesViewLoader />}>
            <AnglerCatchesView
              usersOwnPage={usersOwnPage}
              nickname={nickname}
              totalAnglerCatches={totalAnglerCatches}
            />
          </Suspense>
        )}
        {sortBy === 'posts' && (
          <Suspense fallback={<AnglerPostsViewLoader />}>
            <AnglerPostsView
              className="md:mx-12"
              usersOwnPage={usersOwnPage}
              nickname={nickname}
              user={angler}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
};
